import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { makeStyles, useTheme } from '@mui/styles';

import Moment from 'moment/moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { get as _get } from 'lodash';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { DataGridPro } from '@mui/x-data-grid-pro';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import LockIcon from '@mui/icons-material/Lock';
import UnlockIcon from '@mui/icons-material/LockOpen';

import { LoadingOverlay, NoRowsOverlay } from '../../../../components/xgrid/overlays';
import CopyText from '../../../../components/copytext';
import Validator, { useValidate, checkValidity } from '../../../../components/validator';
import DateTimePicker from '../../../../components/styled/datetimepicker';

import { AccessKeysTab } from './index';
import Auth, { ProfileContext } from '../../../../data/auth-service';
import AccessKeyService, { fields } from '../../../../data/access-keys-service';

import useWindowSize from '../../../../libraries/usewindowsize';
import { IsValidDate } from '../../../../libraries/helpers';

const useStyles = makeStyles(theme => ({
    root: {
    },
    copy: {
        paddingLeft: theme.spacing(1),
        lineHeight: 1.75,
    },
    primary: {
        color: theme.palette.primary.main,
    },
    buttonRight: {
        float: 'right',
        marginLeft: theme.spacing(2),
    },
}));

const TOOLBAR_SIZE = 400;

const Accesskeys = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { state } = useLocation();
    const { height } = useWindowSize();
    const { updateProfile, getProfile } = useContext(ProfileContext);
    // const accessKeysPerms = Auth.permissions.get('AccessKeys');

    const [perms, setPerms] = useState({});
    const [tableHeight, setTableHeight] = useState(800);
    const [accessKeys, setAccessKeys] = useState([]);
    const [selection, setSelection] = useState();
    const [widths, setWidths] = useState(getProfile('access_keys').widths || {});
    const [columns, setColumns] = useState(fields.map(f =>
        f.type === 'date' ? ({
            ...f,
            renderCell: (params) => {
                const value = params.row[f.field];
                if (IsValidDate(value)) {
                    return Moment(value).format(theme.date.long);
                }
                return 'never';
            },
        }) : f.variable === 'ID' ? ({
            ...f,
            renderCell: (params) => {
                return (
                    <span className={classes.primary} style={{ cursor: 'pointer' }}>
                        <CopyText text={params.row[f.field]} />
                    </span>
                );
            },
        }) : f).map(f => ({ ...f, width: widths[f.field] || f.width })));

    const handleAddAccessKey = useCallback(() => {
        navigate('/system/configuration/auth/access_keys/create_access_key', {
            state: { ...state, accessKeys: accessKeys.map(c => c.name) }
        });
    }, [accessKeys, state, navigate]);

    const handleDelete = useCallback(() => {
        if (!selection) return;
        AccessKeyService.deleteAccessKey(selection.id).then(res => {
            setAccessKeys(accessKeys.filter(c => c.ID !== selection.id));
            setSelection(null);
            toast.success('Access key deleted successfully.');
        });
    }, [selection, accessKeys]);

    const handleDeactivate = useCallback(() => {
        if (!selection) return;
        console.log(selection);
        AccessKeyService.deactivateAccessKey(selection.id).then(res => {
            setAccessKeys(current => current.map(c => c.ID === selection.id ? { ...c, active: false } : c));
            setSelection(null);
            toast.success('Access Key deactivated successfully.');
        });
    }, [selection]);

    const toggleDeactivate = useCallback(() => {
        if (!selection) return;

        if (_get(selection, 'row.active')) {
            handleDeactivate();
            return;
        }

        AccessKeyService.activateAccessKey(selection.id).then(res => {
            setAccessKeys(accessKeys.map(c => c.ID === selection.id ? { ...c, active: true } : c));
            setSelection(null);
            toast.success('Access key activated successfully.');
        });
    }, [handleDeactivate, selection, accessKeys]);

    const getRowId = useCallback(r => r.ID, []);

    const handleColumnResize = useCallback((p) => {
        if (!_get(p, 'colDef.field')) return;

        setWidths(current => {
            const widths = {
                ...current,
                [p.colDef.field]: p.width,
            };
            updateProfile('access_keys', {
                ...getProfile('access_keys'),
                widths,
            });
            return widths;
        });
    }, [getProfile, updateProfile]);

    // size hook
    useEffect(() => {
        setTableHeight(Math.max(height - TOOLBAR_SIZE, 300));
    }, [height]);

    useEffect(() => {
        setColumns(current => {
            return current.map(c => {
                const width = widths[c.field];
                return width ? { ...c, width } : c;
            });
        });
    }, [widths]);

    useEffect(() => {
        setPerms(Auth.permissions.get('AccessKeys'));
    }, []);

    useEffect(() => {
        perms.write && (async () => {
            AccessKeyService.listAccessKeys().then(res => {
                setAccessKeys(res || []);
            });
        })();
    }, [perms]);

    return perms && perms.write ? (
        <Grid container>
            <Grid item xs={12}>
                <Box mb={3} display="flex" justifyContent="flex-end">
                    <Button
                        variant="contained"
                        aria-label="Add Access Key"
                        onClick={handleAddAccessKey}>
                        <AddIcon/> Add Access Key
                    </Button>
                </Box>
                <Box height={ tableHeight }>
                    <DataGridPro
                        components={{
                            Header: () => (
                                <Box p={1} display="flex" justifyContent="flex-end" borderBottom="1px solid #666666">
                                    <IconButton
                                        disabled={!selection}
                                        variant="contained"
                                        name="delete"
                                        aria-label="delete"
                                        title="Delete"
                                        onClick={handleDelete}
                                        size="large"><DeleteIcon />
                                    </IconButton>
                                    <IconButton
                                        disabled={!selection}
                                        variant="contained"
                                        name="deactivate"
                                        aria-label="deactivate"
                                        title={_get(selection, 'row.active') ? 'Deactivate' : 'Activate'}
                                        onClick={toggleDeactivate}
                                        size="large">{_get(selection, 'row.active') ? <LockIcon/> : <UnlockIcon />}
                                    </IconButton>
                                </Box>
                            ),
                            LoadingOverlay: LoadingOverlay,
                            NoRowsOverlay: NoRowsOverlay,
                        }}
                        rows={accessKeys}
                        getRowId={getRowId}
                        columns={columns}
                        onRowClick={setSelection}
                        sortingOrder={['desc', 'asc']}
                        onColumnResize={handleColumnResize}
                        disableMultipleSelection
                        disableColumnSelector={true}
                        disableColumnMenu={true}/>
                </Box>
            </Grid>
        </Grid>
    ) : null;
};

const CreateAccessKey = (props) => {
    const classes = useStyles();
    const theme = useTheme();
    const navigate = useNavigate();
    const { state } = useLocation();
    const accessKeyForm = useValidate({});

    const [accessKey, setAccessKey] = useState({ name: '', expiration: null });
    const [created, setCreated] = useState(false);
    const [accessKeys, setAccessKeys] = useState([]);

    // expiration can be empty or some time in the future
    const validExpiration = useCallback((expiration) => {
        return expiration == null || expiration === '' ||
          (Moment(expiration).isValid() && Moment(expiration).isAfter(Moment()));
    }, []);

    const handleUpdateClientName = useCallback((e, validID, valid) => {
        accessKeyForm.set({ ...accessKeyForm.get(), [validID]: valid });
        setAccessKey(current => ({ ...current, name: e.target.value }));
    }, [accessKeyForm]);

    const handleUpdateClientExpiration = useCallback((date) => {
        setAccessKey(current => ({ ...current, expiration: date }));
    }, []);

    const handleCreateClient = useCallback(() => {
        AccessKeyService.createAccessKey(accessKey).then(res => {
            setCreated(true);
            setAccessKey(res);
            toast.success('Access key created successfully. Please copy the key and secret.');
        });
    }, [accessKey]);

    const handleDone = useCallback(() => {
        if (state && state.fromUser) {
            navigate(-1);
        } else {
            navigate('/system/configuration/auth', { state: { tab: AccessKeysTab } });
        }
    }, [state, navigate]);

    const expiration = useMemo(() => {
        return IsValidDate(accessKey.expiration) ? accessKey.expiration : null;
    }, [accessKey.expiration]);

    const isValid = () => {
        if (!validExpiration(accessKey.expiration) || !accessKey.name) {
            return false;
        }
        return checkValidity(accessKeyForm.get());
    };

    useEffect(() => {
        if (!state.accessKeys) {
            CreateAccessKey.listUserClients().then(res => {
                setAccessKeys(res.map(c => c.name));
            });
        } else {
            setAccessKeys(state.accessKeys);
        }
    }, [state]);

    return (
        <Grid container spacing={2} direction="row" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
                <Paper elevation={3}>
                    <Box p={5}>
                        <Grid container spacing={5}>
                            <Grid item xs={6}>
                                <Validator
                                    valueType='username'
                                    errorText="enter a valid unique name"
                                    label="Name"
                                    aria-label="name"
                                    variant="outlined"
                                    margin="normal"
                                    fullWidth
                                    required={true}
                                    allowEmpty={false}
                                    value={accessKey.name}
                                    unique={[...(accessKeys || []), accessKey.name || '']}
                                    name="name"
                                    disabled={created}
                                    onChange={handleUpdateClientName}
                                />
                                <Grid item>
                                    <DateTimePicker
                                        required={false}
                                        clearable={true}
                                        allowEmpty={true}
                                        label="Expiration Date"
                                        aria-label="expiration"
                                        value={expiration}
                                        defaultValue={null}
                                        onChange={handleUpdateClientExpiration}
                                        inputFormat={theme.date.short}
                                        disableMaskedInput={true}
                                        showToolbar={true}
                                        disabled={created}
                                        ampm={false}/>
                                </Grid>
                            </Grid>
                            <Grid item display="flex" xs={12}>
                                <Fade in={created}>
                                    <Alert variant="outlined" severity="success" color="warning">
                                        Please copy your Access key and Secret to a safe location.
                                        Your secret will not be displayed again.

                                        Do not share this information with anyone.
                                    </Alert>
                                </Fade>
                            </Grid>
                            {created && <Grid item xs={12}>
                                <Grid item display="flex">
                                    <Typography variant="button">Access Key:</Typography>
                                    <Typography variant="subtitle2" className={classes.copy}>
                                        <CopyText text={accessKey.ID} />
                                    </Typography>
                                </Grid>
                                <Grid item display="flex" style={{ marginTop: 10 }}>
                                    <Typography variant="button">Secret:</Typography>
                                    <Typography variant="subtitle2" className={classes.copy}>
                                        <CopyText text={accessKey.Secret} />
                                    </Typography>
                                </Grid>
                                <Grid item display="flex" xs={12} style={{ marginTop: 10, cursor: 'pointer' }} >
                                    <Typography variant="button" className={classes.primary}>
                                        <CopyText copy={`Key: ${accessKey.ID}, Secret: ${accessKey.Secret}`} text='Copy both to clipboard'/>
                                    </Typography>
                                </Grid>
                            </Grid>
                            }
                            <Grid item xs={12}>
                                {!created && <Box display="flex" justifyContent="flex-end">
                                    <Box ml={2}><Button variant="contained" color="secondary" aria-label="Cancel"
                                        onClick={handleDone}>Cancel</Button></Box>
                                    <Box ml={2}><Button
                                        variant="contained"
                                        color="primary"
                                        aria-label="Create"
                                        onClick={handleCreateClient}
                                        disabled={!isValid()}>
                                        Create
                                    </Button></Box>
                                </Box>}
                                {created && <Box display="flex" justifyContent="flex-end">
                                    <Box ml={2}><Button
                                        variant="contained"
                                        color="primary"
                                        aria-label="Secret Copied"
                                        onClick={handleDone}>
                                        Secret Copied
                                    </Button></Box>
                                </Box>}
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>
            </Grid>
        </Grid>

    );
};

export {
    Accesskeys as default,
    CreateAccessKey,
};
